import { z } from 'zod';

// The SERVER side of step one of the signup: `captureLead` (email + Turnstile
// on "Continue") and the Customer.io identify it fires behind the gate.
// Naming: domain.subject_verb, past tense.
//
// Why these exist: a Sept 2026 audit found step-one emails with no Customer.io
// delivery record and no way to say why. Three silent branches could each
// cause it — Turnstile rejected server-side (C.io skipped, no trace), the
// identify timing out or non-2xx (one 4s attempt, `void`, silent catch), and
// an internal/reserved address skipped. That is ADR 0002's silent-failure
// pattern on the money path's front door.
//
// Emitted from the API through `safeEmit`; never put the email in a payload.

// Why an identify did not land. `skipped` = refused by policy the caller can
// see (non-prod Railway env, internal/reserved address): expected, never
// retried. `failed` = everything else: Customer.io rejected, timed out, the
// network dropped, or credentials are missing (on prod that is a fault).
// cioFetch folds those into one `false`, so they cannot be told apart here.
const LEAD_IDENTIFY_FAILURE_REASONS = ['skipped', 'failed'] as const;

export const leadSchemas = {
  /**
   * captureLead returned. `turnstile` is what the server-side gate decided:
   * `verified` (Customer.io + Meta run), `rejected` (token present, Cloudflare
   * said no), `missing` (tokenless blur: Mission Control only, by design).
   */
  'lead.capture_completed': z.object({
    turnstile: z.enum(['verified', 'rejected', 'missing']),
  }),
  /**
   * A Turnstile token arrived and Cloudflare's siteverify refused it, so
   * Customer.io and Meta were silently skipped for a real form submit.
   * `variant` is which site key issued the challenge.
   */
  'lead.turnstile_rejected': z.object({
    variant: z.enum(['inapp', 'managed']).optional(),
  }),
  /** The step-one Customer.io identify landed. `attempts` > 1 means the retry saved it. */
  'lead.identify_synced': z.object({ attempts: z.number().int().positive() }),
  /** The step-one Customer.io identify did not land after every attempt. */
  'lead.identify_failed': z.object({
    attempts: z.number().int().nonnegative(),
    reason: z.enum(LEAD_IDENTIFY_FAILURE_REASONS),
  }),
};
